import React from "react";
import { Box, Typography, Chip, IconButton, Grid } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Section from "../../../layout/Section";
import OutputField from "../../../common/display/OutputField";
import TextEditorField from "../../../common/editor/TextEditorField";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import { formatNumber } from "../../../../utils/formatters";
import {
  getStatusColor,
  getStatusLabel,
} from "../../../../utils/colors/parcelStatusUtils";
import { colors } from "../../../../styles/colors";
import { formatParcelLabel } from "../../../../utils/parcelLabel";
import type { ParcelData } from "../../../map";

interface ParcelsSectionProps {
  parcels: any[];
  selectedParcelId: string | null;
  selectedParcel: ParcelData | null;
  isLoadingParcels: boolean;
  onParcelClick: (parcelId: string | null) => void;
  onBack?: () => void;
  parcelNotizen: string;
  setParcelNotizen: (value: string) => void;
  canEdit: boolean;
  isEditMode: boolean;
  context?: "parcel" | "owner" | null;
}

const ParcelsSection: React.FC<ParcelsSectionProps> = ({
  parcels,
  selectedParcelId,
  selectedParcel,
  isLoadingParcels,
  onParcelClick,
  onBack,
  parcelNotizen,
  setParcelNotizen,
  canEdit,
  isEditMode,
  context,
}) => {
  const parcelColumns: TableColumn[] = [
    {
      key: "relevant",
      label: "Relevant",
      align: "center",
      width: "10%",
      sortable: true,
    },
    {
      key: "flurstueck",
      label: "Flurstück",
      align: "left",
      width: "35%",
      sortable: true,
    },
    {
      key: "flaeche",
      label: "Fläche",
      align: "right",
      width: "25%",
      unit: "m²",
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      align: "right",
      width: "30%",
      sortable: true,
    },
  ];

  const renderRelevant = (
    _column: TableColumn,
    _row: TableData,
    value: any,
  ) => {
    if (!value) return null;
    return (
      <CheckCircleIcon
        sx={{ fontSize: 18, color: colors.status.success.main }}
      />
    );
  };

  const renderParcelStatus = (
    _column: TableColumn,
    _row: TableData,
    value: any,
  ) => {
    const status = value || "Kein Kontakt";
    const statusColor = getStatusColor(status);
    return (
      <Chip
        label={getStatusLabel(status)}
        size="small"
        variant="filled"
        sx={{
          backgroundColor: statusColor,
          color: colors.utility.black,
          fontWeight: 500,
        }}
      />
    );
  };

  const renderParcelFlaeche = (
    _column: TableColumn,
    _row: TableData,
    value: any,
  ) => {
    if (value === null || value === undefined) return "-";
    return formatNumber(value, 2) + " m²";
  };

  const parcelsTableData: TableData[] = parcels.map((parcel) => ({
    id: parcel.id,
    relevant: parcel.relevant ?? false,
    flurstueck: formatParcelLabel(parcel),
    status: parcel.status || "Kein Kontakt",
    flaeche: parcel.projektFlaeche ?? parcel.flaeche,
  }));

  const handleParcelRowClick = (row: TableData) => {
    onParcelClick(row.id as string);
  };

  // Verwende selectedParcel für Detail-Ansicht, falls vorhanden, sonst suche in parcels
  const selectedParcelForDetail =
    selectedParcel ||
    (selectedParcelId ? parcels.find((p) => p.id === selectedParcelId) : null);

  // Bestimme Titel basierend auf Kontext
  const getSectionTitle = () => {
    if (context === "owner") {
      return "Flurstücke des Eigentümers";
    }
    if (context === "parcel" || selectedParcelId) {
      return "Ausgewähltes Flurstück";
    }
    return "Flurstücke";
  };

  return (
    <Section title={getSectionTitle()} noPadding={!selectedParcelForDetail}>
      {selectedParcelForDetail ? (
        // Detail-Ansicht für ausgewähltes Flurstück
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <OutputField
                label="Flurstück"
                value={formatParcelLabel(selectedParcelForDetail)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              {(selectedParcelForDetail.projektFlaeche ??
                selectedParcelForDetail.flaeche) !== null &&
              (selectedParcelForDetail.projektFlaeche ??
                selectedParcelForDetail.flaeche) !== undefined ? (
                <OutputField
                  label="Fläche"
                  value={
                    formatNumber(
                      selectedParcelForDetail.projektFlaeche ??
                        selectedParcelForDetail.flaeche,
                      2,
                    ) + " m²"
                  }
                />
              ) : (
                <OutputField label="Fläche" value="-" />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <OutputField
                label="Status"
                value={
                  <Chip
                    label={getStatusLabel(selectedParcelForDetail.status)}
                    size="small"
                    variant="filled"
                    sx={{
                      backgroundColor: getStatusColor(
                        selectedParcelForDetail.status,
                      ),
                      color: colors.utility.black,
                      fontWeight: 500,
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
          {/* Notizen-Feld */}
          <Box sx={{ mt: 4 }}>
            <TextEditorField
              value={parcelNotizen}
              onChange={setParcelNotizen}
              placeholder="Notizen zum Flurstück (Rich Text-Format unterstützt)"
              label="Notizen"
              readOnly={!isEditMode || !canEdit}
              labelBackgroundColor="white"
              minHeight="60px"
            />
          </Box>
          {/* Back-Button mit Text */}
          {onBack && (
            <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  padding: 0.5,
                  minWidth: 32,
                  height: 32,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    color: "text.primary",
                  },
                }}
                aria-label="Zurück zur Übersicht"
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "bold",
                    lineHeight: 1,
                  }}
                >
                  &lt;
                </Typography>
              </IconButton>
              <Typography
                variant="body2"
                onClick={onBack}
                sx={{
                  color: "text.secondary",
                  cursor: "pointer",
                  "&:hover": {
                    color: "text.primary",
                  },
                }}
              >
                Zurück
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Tabellen-Ansicht für alle Flurstücke
        <BaseTable
          columns={parcelColumns}
          data={parcelsTableData}
          onRowClick={handleParcelRowClick}
          selectedRowId={selectedParcelId || undefined}
          loading={isLoadingParcels}
          emptyMessage="Keine Flurstücke verfügbar"
          renderCell={(column, row, value) => {
            if (column.key === "relevant") {
              return renderRelevant(column, row, value);
            }
            if (column.key === "status") {
              return renderParcelStatus(column, row, value);
            }
            if (column.key === "flaeche") {
              return renderParcelFlaeche(column, row, value);
            }
            return value;
          }}
          stickyHeader={true}
          maxHeight={400}
          showBorder={false}
          variant="compact"
          minWidth={400}
          defaultSortColumn="flaeche"
          defaultSortDirection="desc"
          disablePagination={true}
        />
      )}
    </Section>
  );
};

export default ParcelsSection;
